import { HttpService } from "@app/core/services/http/http.service";
import { Injectable, inject } from "@angular/core";
import {
  TranslationsAdapter,
  TranslationAdapter,
  Translation,
  Translations,
  TranslationsList
} from "@app/portals/admin-modules/application-settings/application-settings.model";
import {
  Application,
  ApplicationAdapter,
  Applications,
  ApplicationsAdapter,
  ApplicationEnvironments,
  ApplicationEnvironmentsAdapter,
  ApplicationEnvironment,
  ApplicationEnvironmentAdapter,
  ApplicationEnviromentsMasterListAdapter,
  ApplicationEnviromentsMasterList,
  ApplicationEnviromentsReportsPlusListAdapter,
  ApplicationEnviromentsReportsPlus
} from "@app/portals/admin-modules/application-settings/applications/applications.model";
import { IPLUS_URL, RPLUS_URL } from "@app/shared/constants/url.constants";
import { map } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class ApplicationsService {
  private http = inject(HttpService);

  fetchApplications() {
    const applicationsAdapter = new ApplicationsAdapter();
    return this.http
      .get<Applications>(IPLUS_URL.APPLICATIONS_LIST)
      .pipe(map((response: any) => applicationsAdapter.adapt(response)));
  }

  /**
   * Fetch active applications by filterType.
   * '*' => fetch all active records
   * 'A' => fetch all admin applications
   * 'M' => fetch all main applications
   * 'C' => fetch all common applications
   * 'O' => fetch all other applications
   * @param filterType
   * @returns
   */
  fetchFilteredApplications(filterType: "*" | "A" | "M" | "C" | "O" = "*") {
    const applicationsAdapter = new ApplicationsAdapter();
    return this.http
      .addDataParams({ type: filterType })
      .get<Applications>(IPLUS_URL.APPLICATIONS_FILTER_LIST)
      .pipe(map((response: any) => applicationsAdapter.adapt(response)));
  }

  fetchApplicationById(data: Application) {
    const applicationAdapter = new ApplicationAdapter();
    return this.http
      .addDataParams(data)
      .get<Application>(IPLUS_URL.APPLICATIONS_MAINTENANCE)
      .pipe(map((response: any) => applicationAdapter.adapt(response)));
  }

  updateApplication(data: Application) {
    const applicationAdapter = new ApplicationAdapter();
    return this.http
      .addDataParams(data)
      .put(IPLUS_URL.APPLICATIONS_MAINTENANCE, "")
      .pipe(map((response: any) => applicationAdapter.adapt(response)));
  }

  fetchTranslations(applicationId: string) {
    const applicationsAdapter = new TranslationsAdapter();
    return this.http
      .addDataParams({ id: applicationId })
      .get<Translations>(IPLUS_URL.APPLICATIONS_TRANSLATIONS_LIST)
      .pipe(map((response: any) => applicationsAdapter.adapt(response)));
  }

  fetchTranslationById(data: Translation) {
    const applicationAdapter = new TranslationAdapter();
    return this.http
      .addDataParams(data)
      .get<Translation>(IPLUS_URL.APPLICATIONS_TRANSLATIONS_MAINTENANCE)
      .pipe(map((response: any) => applicationAdapter.adapt(response)));
  }

  createTranslation(data: Translation) {
    const applicationAdapter = new TranslationAdapter();
    return this.http
      .addDataParams(data)
      .post<Translation>(IPLUS_URL.APPLICATIONS_TRANSLATIONS_MAINTENANCE, "")
      .pipe(map((response: any) => applicationAdapter.adapt(response)));
  }

  updateTranslation(data: Translation) {
    const applicationAdapter = new TranslationAdapter();
    return this.http
      .addDataParams(data)
      .put<Translation>(IPLUS_URL.APPLICATIONS_TRANSLATIONS_MAINTENANCE, "")
      .pipe(map((response: any) => applicationAdapter.adapt(response)));
  }

  deleteTranslation(id: string, data: TranslationsList) {
    const applicationAdapter = new TranslationAdapter();
    return this.http
      .addDataParams({ id: id, language: data.language })
      .delete<Translation>(IPLUS_URL.APPLICATIONS_TRANSLATIONS_DELETE)
      .pipe(map((response: any) => applicationAdapter.adapt(response)));
  }

  fetchApplicationEnviroments(applicationId: string) {
    const respAdapter = new ApplicationEnvironmentsAdapter();
    return this.http
      .addDataParams({ applicationId: applicationId })
      .get<ApplicationEnvironments>(IPLUS_URL.APPLICATIONS_ENVIRONMENTS_LIST)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  fetchApplicationEnvironmentById(data: ApplicationEnvironment) {
    const respAdapter = new ApplicationEnvironmentAdapter();
    return this.http
      .addDataParams(data)
      .get<ApplicationEnvironment>(IPLUS_URL.APPLICATIONS_ENVIRONMENTS_MAINTENANCE)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  createApplicationEnvironment(data: ApplicationEnvironment) {
    const respAdapter = new ApplicationEnvironmentAdapter();
    return this.http
      .addDataParams(data)
      .post<ApplicationEnvironment>(IPLUS_URL.APPLICATIONS_ENVIRONMENTS_MAINTENANCE, "")
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  updateApplicationEnvironment(data: ApplicationEnvironment) {
    const respAdapter = new ApplicationEnvironmentAdapter();
    return this.http
      .addDataParams(data)
      .put<ApplicationEnvironment>(IPLUS_URL.APPLICATIONS_ENVIRONMENTS_MAINTENANCE, "")
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  deleteApplicationEnvironment(appId: string, envId: string) {
    const respAdapter = new ApplicationEnvironmentAdapter();
    return this.http
      .addDataParams({ applicationId: appId, environmentId: envId })
      .delete(IPLUS_URL.APPLICATIONS_ENVIRONMENTS_DELETE)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  fetchAllApplicationEnviroments(appId: string) {
    const respAdapter = new ApplicationEnviromentsMasterListAdapter();
    return this.http
      .addDataParams({ applicationId: appId })
      .get<ApplicationEnviromentsMasterList[]>(IPLUS_URL.APPLICATIONS_ENVIRONMENTS_MASTER_LIST)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  fetchReportsPlusApplicationEnviroments() {
    const respAdapter = new ApplicationEnviromentsReportsPlusListAdapter();
    return this.http
      .get<ApplicationEnviromentsReportsPlus[]>(RPLUS_URL.APPLICATIONS_ENVIRONMENTS_RPLUS_LIST)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }
}
